import { useState } from "react";
import { X, FileText, Clock } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Textarea } from "../../../../components/ui/textarea";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import { AsambleaRecord } from "../../../../api/asambleaService";
import { insertarPreguntaAsamblea } from "../../../../api/preguntaAsambleaService";
import Swal from "sweetalert2";

interface CreatePreguntaAsambleaModalProps {
  isOpen: boolean;
  asamblea: AsambleaRecord | null;
  onClose: (created: boolean) => void;
}

export default function CreatePreguntaAsambleaModal({
  isOpen,
  asamblea,
  onClose,
}: CreatePreguntaAsambleaModalProps) {
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [texto, setTexto] = useState("");
  const [conTiempo, setConTiempo] = useState<number>(0);
  const [minutos, setMinutos] = useState<number>(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!texto.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el texto de la pregunta",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (conTiempo === 1 && (!minutos || minutos <= 0)) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese los minutos cuando la pregunta usa tiempo",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const usuarioAdministrador = session?.email || "";
      const asambleaCodigo = String(asamblea?.asa_codigo || "");

      if (!usuarioAdministrador || !asambleaCodigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        asamblea_codigo: asambleaCodigo,
        usuario_administrador: usuarioAdministrador,
        texto: texto.trim(),
        con_tiempo: String(conTiempo),
        minutos: String(minutos),
      };

      const response = await insertarPreguntaAsamblea(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Pregunta creada!",
          text: response.message || "La pregunta se ha creado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        setTexto("");
        setConTiempo(0);
        setMinutos(0);
        onClose(true);
      } else {
        throw new Error(response.message || "Error al crear la pregunta");
      }
    } catch (error) {
      console.error("Error al crear pregunta:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al crear la pregunta";
      const mensajeTraducido = errorMessage.includes("Request failed")
        ? "Error al crear la pregunta. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !asamblea) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <FileText size={28} />
              Crear Nueva Pregunta
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Asamblea: {asamblea.asa_titulo}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText size={16} className="inline mr-1" />
              Texto de la pregunta <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribe el texto de la pregunta..."
              disabled={isSubmitting}
              className="min-h-[120px]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock size={16} className="inline mr-1" />
              ¿Usa tiempo? <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="con_tiempo"
                  value="1"
                  checked={conTiempo === 1}
                  onChange={() => setConTiempo(1)}
                  disabled={isSubmitting}
                  className="text-military-500 focus:ring-military-500"
                />
                <span className="text-sm font-medium text-gray-700">SI</span>
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="con_tiempo"
                  value="0"
                  checked={conTiempo === 0}
                  onChange={() => setConTiempo(0)}
                  disabled={isSubmitting}
                  className="text-military-500 focus:ring-military-500"
                />
                <span className="text-sm font-medium text-gray-700">NO</span>
              </label>
            </div>
          </div>

          {conTiempo === 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Minutos <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="1"
                value={minutos || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    setMinutos(value === "" ? 0 : parseInt(value));
                  }
                }}
                placeholder="Ingrese los minutos"
                disabled={isSubmitting}
                required={conTiempo === 1}
              />
              <p className="text-xs text-gray-500 mt-1">
                Solo se permiten números enteros positivos
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <Button
              type="button"
              onClick={() => onClose(false)}
              variant="outline"
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-military-500 hover:bg-military-600 text-white"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  Guardando...
                </>
              ) : (
                "Guardar Pregunta"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
